import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import dayjs from "dayjs";

// Colores por estado de excursion
const EXCURSION_STATUS_COLORS = {
  PENDING: "#faad14", // Naranja
  IN_PROGRESS: "#1890ff", // Azul
  STOP_SELLING: "#ff4d4f", // Rojo
  FINISHED: "#52c41a", // Verde
};

// Colores por estado de tarea
const TASK_STATUS_COLORS = {
  PENDING: "#F59E0B",
  IN_PROGRESS: "#3B82F6",
  COMPLETED: "#10B981",
  CANCELLED: "#6B7280",
};

// Funcion para agrupar por fecha
const groupBy = (array, key) => {
  return array.reduce((result, item) => {
    const groupKey = item[key];
    if (!result[groupKey]) {
      result[groupKey] = [];
    }
    result[groupKey].push(item);
    return result;
  }, {});
};

const CalendarView = ({
  excursions = [],
  groups = [],
  tasks = [],
  filters = { showGroups: true, showExcursions: true, showTasks: true },
  onDatesSet,
  onEventClick,
  onDateClick,
}) => {
  // 1. Excursiones como eventos principales
  const excursionEvents = excursions.map((exc) => ({
    id: `exc-${exc.id}`,
    title: `${exc.excursionType?.name || "Excursión"} (${exc.passengersCount || 0} pax)`,
    start: exc.day,
    allDay: true,
    backgroundColor: EXCURSION_STATUS_COLORS[exc.status] || "#1890ff",
    borderColor: EXCURSION_STATUS_COLORS[exc.status] || "#1890ff",
    editable: false,
    extendedProps: { type: "excursion", data: exc },
  }));

  // 2. Agrupar grupos por fecha de ingreso (check-in)
  const checkInByDate = groupBy(groups, "checkInDate");
  const checkInEvents = Object.entries(checkInByDate).map(([date, grps]) => ({
    id: `checkin-${date}`,
    title: `Check In ↓ (${grps.length})`,
    start: date,
    allDay: true,
    backgroundColor: "#1890ff",
    borderColor: "#1890ff",
    classNames: ["calendar-indicator", "calendar-indicator-checkin"],
    editable: false,
    extendedProps: { type: "checkin", data: grps, date },
  }));

  // 3. Agrupar grupos por fecha de egreso (check-out / rendición)
  const checkOutByDate = groupBy(groups, "checkOutDate");
  const checkOutEvents = Object.entries(checkOutByDate).map(([date, grps]) => ({
    id: `checkout-${date}`,
    title: `Check Out ↑ (${grps.length})`,
    start: date,
    allDay: true,
    backgroundColor: "#faad14",
    borderColor: "#faad14",
    classNames: ["calendar-indicator", "calendar-indicator-checkout"],
    editable: false,
    extendedProps: { type: "checkout", data: grps, date },
  }));

  // 4. Tareas con fecha como eventos
  const taskEvents = tasks
    .filter((task) => task.date) // Solo tareas con fecha
    .map((task) => ({
      id: `task-${task.id}`,
      title: `${task.isRecurring ? "↻ " : ""}${task.title}`,
      start: task.date,
      allDay: true,
      backgroundColor: task.color || TASK_STATUS_COLORS[task.status],
      borderColor: task.color || TASK_STATUS_COLORS[task.status],
      classNames: [
        "calendar-indicator",
        "calendar-indicator-task",
        task.isRecurring ? "calendar-indicator-recurring" : "",
      ].filter(Boolean),
      editable: false,
      extendedProps: { type: "task", data: task },
    }));

  // Combinar eventos segun filtros activos
  let allEvents = [];

  if (filters.showExcursions) {
    allEvents = [...allEvents, ...excursionEvents];
  }
  if (filters.showGroups) {
    allEvents = [...allEvents, ...checkInEvents, ...checkOutEvents];
  }
  if (filters.showTasks) {
    allEvents = [...allEvents, ...taskEvents];
  }

  return (
    <FullCalendar
      plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
      initialView="dayGridMonth"
      headerToolbar={{
        left: "prev,next today",
        center: "title",
        right: "dayGridMonth,timeGridWeek",
      }}
      locale="es"
      events={allEvents}
      editable={false}
      selectable={true}
      dayMaxEvents={5}
      datesSet={onDatesSet}
      eventClick={onEventClick}
      dateClick={onDateClick}
      height="auto"
      buttonText={{
        today: "Hoy",
        month: "Mes",
        week: "Semana",
      }}
      eventOrder="title"
    />
  );
};

export default CalendarView;
